import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../atoms";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import type { StartupSettingsInitialData } from "../StartupSettingsForm/StartupSettingsForm";
import type { UpdateStartupData } from "@/types/startup.types";

interface TeamSettingsValues {
  allowInvites: boolean;
  requireApproval: boolean;
}

export interface TeamAccessSettingsProps {
  initialData?: StartupSettingsInitialData;
  onSubmit: (data: Pick<UpdateStartupData, "teamSettings">) => Promise<void>;
  isLoading?: boolean;
}

export const TeamAccessSettings: React.FC<TeamAccessSettingsProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
}) => {
  const defaultValues = useMemo(
    (): TeamSettingsValues => ({
      allowInvites: initialData?.teamSettings?.allowInvites ?? true,
      requireApproval: initialData?.teamSettings?.requireApproval ?? false,
    }),
    [initialData],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<TeamSettingsValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <SettingsSectionCard
      title="Team access"
      description="Control who can be invited and whether new members must accept before joining."
    >
      <form
        onSubmit={handleSubmit(async (data) => {
          await onSubmit({ teamSettings: data });
          reset(data);
        })}
        className="space-y-4"
      >
        <label className="flex items-start gap-3 rounded-[14px] border border-white/10 bg-surface/20 px-4 py-3">
          <input
            type="checkbox"
            {...register("allowInvites")}
            className="mt-0.5 rounded border-white/10 text-brand shadow-none focus:ring-brand/40"
          />
          <span>
            <span className="block text-sm font-medium text-white">
              Allow team invites
            </span>
            <span className="mt-0.5 block text-xs text-white/50">
              Owners can invite collaborators by email from the sharing section below.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-[14px] border border-white/10 bg-surface/20 px-4 py-3">
          <input
            type="checkbox"
            {...register("requireApproval")}
            className="mt-0.5 rounded border-white/10 text-brand shadow-none focus:ring-brand/40"
          />
          <span>
            <span className="block text-sm font-medium text-white">
              Require invite acceptance
            </span>
            <span className="mt-0.5 block text-xs text-white/50">
              New members must accept before they can access this burn chart.
            </span>
          </span>
        </label>

        <div className="flex justify-end border-t border-white/10 pt-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={isLoading || !isDirty}
          >
            {isLoading ? "Saving..." : "Save team settings"}
          </Button>
        </div>
      </form>
    </SettingsSectionCard>
  );
};
